import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FuseConfigService } from '@fuse/services/config.service';
import { AuthenticationService } from 'app/core/auth/services/authentication.service';
import { Language } from 'app/services/i18n.languages';
import { I18nService } from 'app/services/i18n.service';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { catchError, map } from 'rxjs/operators';
import { CurrentUserService } from '../../services/current-user.service';
import { FuseInnerScrollDirective } from '../../../../../@fuse/directives/fuse-inner-scroll/fuse-inner-scroll.directive';
import { MatFormField, MatInput, MatError, MatSuffix, MatLabel } from '@angular/material/input';
import { ButtonTypeDirective } from '../../../../modules/global/directives/button-type.directive';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { DurationComponent } from '../../../../modules/common/datetime/duration/duration.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { SubmitDirective } from '../../../../modules/global/directives/submit.directive';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { TranslatePipe, _ } from '@ngx-translate/core';
import { AuthLoginStatusEnum } from 'app/models/auth.model';
import { GlobalErrorsDirective } from 'app/modules/global/directives/form-validation/global-errors.directive';
import { ThemeService } from 'app/modules/layout/components/toolbar/theme.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FuseInnerScrollDirective,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatError,
    ButtonTypeDirective,
    MatIconButton,
    MatSuffix,
    MatIcon,
    DurationComponent,
    MatCheckbox,
    RouterLink,
    MatButton,
    SubmitDirective,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    TranslatePipe,
    GlobalErrorsDirective,
    MatLabel,
  ],
})
export class LoginComponent implements OnInit {
  private readonly submitButton = viewChild('submitButton', { read: ElementRef });

  loginForm: FormGroup;

  readonly showPassword = signal(false);

  readonly LoginErrorsEnum = LoginErrorsEnum;

  readonly ThemeService = inject(ThemeService);

  readonly errorAuth = signal<LoginErrorsEnum>(null);
  readonly retryAfter = signal<number>(null);

  readonly languages: Language[] = environment.languages ? I18nService.languages || [] : [];
  readonly currentLanguage = computed(() => this._i18nService.currentLanguage);

  private readonly _fuseConfigService = inject(FuseConfigService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _i18nService = inject(I18nService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    // Configure the layout
    this._fuseConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        toolbar: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        sidepanel: {
          hidden: true,
        },
      },
    };
  }

  setLanguage(lang: Language): void {
    if (lang.code !== this._i18nService.currentLanguageCode()) {
      this._i18nService.setLanguage(lang.code);
      this._toastrService.success(this._i18nService.translate(_('Nuova lingua: {language}'), { language: lang.name }));
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.loginForm = this._formBuilder.group({
      username: ['', [Validators.required]],
      password: ['', Validators.required],
      remember: [true],
    });
    this.loginForm.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.errorAuth.set(null);
    });
  }

  togglePasswordVisibility(event: MouseEvent) {
    this.showPassword.set(!this.showPassword());
  }

  keypressEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.submitButton()?.nativeElement.click();
    }
  }

  public onSubmit = () =>
    this._authenticationService.login(
      this.loginForm.controls.username.value,
      this.loginForm.controls.password.value,
      this.loginForm.controls.remember.value,
    );

  beforeSubmit() {
    this.errorAuth.set(null);
    this.retryAfter.set(null);
  }

  afterSubmit(response) {
    // GOT TOKEN
    if (!response || response?.status === AuthLoginStatusEnum.auth2faRequired) {
      this.errorAuth.set(LoginErrorsEnum.required2FA);
    } else {
      // GET USER DETAILS
      this._currentUserService
        .loadUserFromRemote()
        .pipe(
          map((response) => {
            if (response?.enabled === false) {
              this.errorAuth.set(LoginErrorsEnum.userDisabled);
              this._authenticationService.logout();
              throw new Error('User disabled');
            }
            return response;
          }),
          catchError((error) => {
            // GET USER DETAILS FAILED
            this.errorAuth.set(LoginErrorsEnum.serverError);
            this._authenticationService.logout();
            throw new Error(error);
          }),
          takeUntilDestroyed(this._destroyRef),
        )
        .subscribe(() => {
          // GET USER DETAILS SUCCESS
          // TRY TO REDIRECT TO RETURN URL
          const returnUrl = this._activatedRoute.snapshot.queryParams?.['returnUrl'];

          if (returnUrl) {
            this._router
              .navigateByUrl(returnUrl)
              .then((success) => {
                if (!success) {
                  this._router.navigate(['dashboard']);
                }
              })
              .catch(() => {
                this._router.navigate(['dashboard']);
              });
          } else {
            this._router.navigate(['dashboard']);
          }
        });
    }
  }

  afterError(response) {
    switch (response?.error?.message) {
      case 'Invalid credentials provided': {
        this.errorAuth.set(LoginErrorsEnum.wrongCredentials);
        break;
      }
      case 'User not found': {
        this.errorAuth.set(LoginErrorsEnum.notFound);
        break;
      }
      case 'Too many attempts': {
        this.errorAuth.set(LoginErrorsEnum.wrongCredentials);
        this.retryAfter.set(+response.headers.get('retry-after') || 0);
        break;
      }
      default: {
        this.errorAuth.set(LoginErrorsEnum.serverError);
        break;
      }
    }
  }
}

export enum LoginErrorsEnum {
  required2FA = 'required2FA',
  wrongCredentials = 'wrongCredentials',
  notFound = 'notFound',
  tooManyAttempts = 'tooManyAttempts',
  userDisabled = 'userDisabled',
  serverError = 'serverError',
}
